import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Modal for creating a new order from the parsed AI extraction of a document.
 * Lets the user pick a project and (optionally) a phase, then shows a summary.
 */
const CreateOrderFromDocDialog = ({
  open,
  onOpenChange,
  projects,
  phases,
  orderProject,
  orderPhase,
  parsedFields,
  lineItems,
  totalAmount,
  creatingOrder,
  onOrderProjectChange,
  onOrderPhaseChange,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border border-zinc-200 rounded-none p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-heading font-bold uppercase tracking-wide">Create Order</DialogTitle>
        </DialogHeader>
        <div className="p-6 pt-4 space-y-4">
          <p className="text-sm text-zinc-500">
            Create a new order from the parsed data. Select a project and optionally a phase.
          </p>
          <div>
            <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Project *</label>
            <select
              value={orderProject}
              onChange={e => onOrderProjectChange(e.target.value)}
              data-testid="create-order-project-select"
              className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 focus:outline-none focus:border-primary"
            >
              <option value="">-- Select Project --</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
          </div>
          {orderProject && phases.length > 0 && (
            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Phase (optional)</label>
              <select
                value={orderPhase}
                onChange={e => onOrderPhaseChange(e.target.value)}
                data-testid="create-order-phase-select"
                className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 focus:outline-none focus:border-primary"
              >
                <option value="">-- No Phase --</option>
                {phases.map(p => <option key={p.phase_id} value={p.phase_id}>{p.name}</option>)}
              </select>
            </div>
          )}

          <div className="bg-zinc-50 border border-zinc-200 p-3 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-zinc-500">Vendor</span>
              <span className="font-medium">{parsedFields?.vendor_name || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Line Items</span>
              <span className="font-mono">{lineItems.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Total</span>
              <span className="font-mono font-bold">${totalAmount.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 h-12 bg-white text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!orderProject || creatingOrder}
              data-testid="confirm-create-order-btn"
              className="flex-1 h-12 bg-primary text-black font-heading font-bold uppercase tracking-wider border border-zinc-200 hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {creatingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Order'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrderFromDocDialog;
